import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { mediaFileUrl } from './mediaFileUrl';

// The screens shown before the back office opens (issue #251): staff
// sign-in, Forgot password, Set password (from an invite or reset link),
// and the shared app password a tenant still uses until its first staff
// member sets their own. All share one centred card, see AuthGate.js.

export const FONT = "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";
const INPUT_STYLE = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box' };
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13 };
const LINK_BUTTON = { padding: 0, border: 'none', background: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };

// Pale blue in dev (the same --bg-light the back office uses in dev, App.css)
// so a local sign-in screen (staff or Customer portal) is never
// mistaken for production; NODE_ENV is inlined at build time.
export const AUTH_BACKGROUND = process.env.NODE_ENV !== 'production' ? '#e0f2fe' : '#f6f8fa';

// Mirrors backend/services/staffAuthService.js PASSWORD_RULES — the
// backend is the one that enforces them; this is the live checklist.
export const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'An upper-case letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'A lower-case letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'A digit', test: (pw) => /[0-9]/.test(pw) },
  { label: 'A symbol', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function passwordMeetsRules(password) {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}

export function PasswordRules({ password }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '2px 0 0', fontSize: 12, display: 'grid', gap: 2 }}>
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        return (
          <li key={rule.label} style={{ color: met ? '#1b5e20' : '#5a6b85' }}>
            {met ? '✓' : '○'} {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

// Fetched once per page load and shared by every card, so switching
// between Sign in and Forgot password doesn't flash the logo.
let brandingRequest = null;
function loadBranding() {
  if (!brandingRequest) {
    brandingRequest = axios.get('/api/auth/branding')
      .then((res) => res.data.data)
      .catch(() => { brandingRequest = null; return null; });
  }
  return brandingRequest;
}

function useBranding(enabled) {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadBranding().then((data) => { if (!cancelled) setBranding(data); });
    return () => { cancelled = true; };
  }, [enabled]);
  return branding;
}

// The Customer portal's name, so a Customer arriving from a link or the
// sign-in screen can tell what the site is.
export function portalTitle(companyName) {
  return companyName ? `${companyName} Customer Portal` : 'Customer Portal';
}

// title and branding ({ company_name, logo_id }) default to the back
// office's; the Customer portal (portal/PortalApp.js) passes its own.
// `portal` titles the card with the portal's name from the company name
// (the invite, reset and confirm-email screens). `footer` sits under a rule
// at the bottom of the card.
export function AuthCard({ subtitle, children, title = 'Optim Commerce', branding: brandingOverride, portal = false, footer = null }) {
  const fetchedBranding = useBranding(brandingOverride === undefined);
  const branding = brandingOverride === undefined ? fetchedBranding : brandingOverride;
  const [logoFailed, setLogoFailed] = useState(false);
  const shownTitle = portal ? portalTitle(branding?.company_name) : title;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: AUTH_BACKGROUND, fontFamily: FONT, padding: 16, boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: 32, boxSizing: 'border-box' }}>
        {process.env.NODE_ENV !== 'production' && (
          <div style={{ margin: '-32px -32px 24px', padding: '7px 10px', borderRadius: '12px 12px 0 0', background: '#0f3d67', color: '#ffffff', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em' }}>
            DEVELOPMENT
          </div>
        )}
        {branding?.logo_id && !logoFailed && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <img
              src={mediaFileUrl(branding.logo_id)}
              alt={branding.company_name || 'Company logo'}
              onError={() => setLogoFailed(true)}
              style={{ maxWidth: '100%', maxHeight: 72, objectFit: 'contain' }}
            />
          </div>
        )}
        <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 4 }}>{shownTitle}</div>
        {subtitle && <div style={{ color: '#5a6b85', fontSize: 14, marginBottom: 24 }}>{subtitle}</div>}
        {children}
        {footer && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eef1f5', fontSize: 12, color: '#5a6b85' }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

export function AuthMessage({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: AUTH_BACKGROUND, fontFamily: FONT, color: '#5a6b85', fontSize: 14 }}>
      {children}
    </div>
  );
}

function errorMessage(err, fallback) {
  if (!err.response) return 'Could not reach the server. It may be starting up, so try again in a moment.';
  return err.response.data?.message || fallback;
}

function ForgotPasswordForm({ initialEmail, onBack }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [saving, setSaving] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/forgot-password', { email });
      setSentMessage(res.data.message);
    } catch (err) {
      setError(errorMessage(err, 'Could not send a reset link'));
    }
    setSaving(false);
  };

  if (sentMessage) {
    return (
      <AuthCard subtitle="Check your email">
        <div style={{ fontSize: 14, marginBottom: 20 }}>{sentMessage}</div>
        <button type="button" onClick={onBack} style={LINK_BUTTON}>Back to sign in</button>
      </AuthCard>
    );
  }

  return (
    <AuthCard subtitle="We'll email you a link to choose a new password">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={LABEL_STYLE}>Email</label>
          <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} />
        </div>
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !email.trim()} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Sending...' : 'Send reset link'}
        </button>
        <button type="button" onClick={onBack} style={{ ...LINK_BUTTON, alignSelf: 'flex-start' }}>Back to sign in</button>
      </form>
    </AuthCard>
  );
}

export function SignInScreen({ notice, onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/sign-in', { email, password });
      onSignedIn(res.data.data);
      return;
    } catch (err) {
      setError(errorMessage(err, 'Could not sign in'));
    }
    setSaving(false);
  };

  if (showForgot) {
    return <ForgotPasswordForm initialEmail={email} onBack={() => setShowForgot(false)} />;
  }

  return (
    <AuthCard subtitle="Sign in to continue">
      {notice && <div style={{ background: '#fff8e1', border: '1px solid #f0d58c', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 14 }}>{notice}</div>}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <label style={LABEL_STYLE}>Email</label>
          <input autoFocus type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} />
        </div>
        <div>
          <label style={LABEL_STYLE}>Password</label>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
        </div>
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !email.trim() || !password} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Signing in...' : 'Sign in'}
        </button>
        <button type="button" onClick={() => setShowForgot(true)} style={{ ...LINK_BUTTON, alignSelf: 'flex-start' }}>Forgot password?</button>
      </form>
    </AuthCard>
  );
}

// Opened from the link in an invite or reset email (/set-password or
// /reset-password ?token=...). Signs the person straight in once saved.
export function SetPasswordScreen({ token, onSignedIn, onGoToSignIn }) {
  const [link, setLink] = useState(null); // { purpose, name, email }
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/auth/links/${encodeURIComponent(token || '')}`)
      .then((res) => { if (!cancelled) setLink(res.data.data); })
      .catch((err) => { if (!cancelled) setLinkError(errorMessage(err, 'This link isn\'t valid.')); });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('The two passwords don\'t match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/set-password', { token, password });
      onSignedIn(res.data.data);
      return;
    } catch (err) {
      setError(errorMessage(err, 'Could not set your password'));
    }
    setSaving(false);
  };

  if (linkError) {
    return (
      <AuthCard subtitle="This link can't be used">
        <div style={{ fontSize: 14, marginBottom: 20 }}>{linkError}</div>
        <button type="button" onClick={onGoToSignIn} style={LINK_BUTTON}>Go to sign in</button>
      </AuthCard>
    );
  }
  if (!link) return <AuthMessage>Checking your link...</AuthMessage>;

  return (
    <AuthCard subtitle={link.purpose === 'invite' ? `Welcome, ${link.name}. Choose a password to finish setting up your login.` : `Choose a new password, ${link.name}.`}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="email" autoComplete="username" value={link.email} readOnly hidden />
        <div>
          <label style={LABEL_STYLE}>New password</label>
          <input autoFocus type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
          <PasswordRules password={password} />
        </div>
        <div>
          <label style={LABEL_STYLE}>Confirm new password</label>
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={INPUT_STYLE} />
        </div>
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !passwordMeetsRules(password) || !confirm} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : 'Set password and sign in'}
        </button>
      </form>
    </AuthCard>
  );
}

export function AppPasswordScreen({ error: initialError, onSubmit }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(initialError || '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const result = await onSubmit(password);
    if (result) {
      setError(result);
      setSaving(false);
    }
  };

  return (
    <AuthCard subtitle="Enter the app password to continue">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !password} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Checking...' : 'Continue'}
        </button>
      </form>
    </AuthCard>
  );
}
